import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Camera } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const MEDIOS_PAGO = ["tarjeta", "efectivo", "transferencia", "cheque", "otro"];

/** Alta de una compra: dos toques en el celular (foto, monto, trabajo). Proveedor y
 * descripción son opcionales. Si viene workOrderId/projectId preseteados, se oculta el buscador. */
export const PurchaseDialog = ({ open, onOpenChange, workOrderId, projectId, invalidateKeys = [] }) => {
  const { t } = useT();
  const qc = useQueryClient();
  const preset = Boolean(workOrderId || projectId);

  const [monto, setMonto] = useState("");
  const [fecha, setFecha] = useState(() => new Date().toISOString().slice(0, 10));
  const [medioPago, setMedioPago] = useState("tarjeta");
  const [proveedor, setProveedor] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const [tipo, setTipo] = useState("orden");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (preset || tipo === "sin_asignar" || query.trim().length < 2) {
      setResults([]);
      return;
    }
    const id = setTimeout(async () => {
      try {
        const path = tipo === "orden" ? "/work-orders" : "/projects";
        const { data } = await api.get(path, { params: { q: query, page_size: 8 } });
        setResults((data.items || []).map((it) => ({
          id: it.id, code: it.code,
          label: tipo === "orden" ? `${it.code} · ${it.property_nombre || ""}` : `${it.code} · ${it.company_nombre || it.cliente_nombre || ""}`,
        })));
      } catch (e) {
        setResults([]);
      }
    }, 300);
    return () => clearTimeout(id);
  }, [query, tipo, preset]);

  const reset = () => {
    setMonto(""); setProveedor(""); setDescripcion(""); setFile(null);
    setFecha(new Date().toISOString().slice(0, 10)); setMedioPago("tarjeta");
    setTipo("orden"); setQuery(""); setResults([]); setSelected(null);
  };

  const submit = async () => {
    if (!monto || Number(monto) <= 0) {
      toast.error(t("finance.purchases.form.amount_required"));
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("monto", monto);
      form.append("fecha", fecha);
      form.append("medio_pago", medioPago);
      if (proveedor) form.append("proveedor", proveedor);
      if (descripcion) form.append("descripcion", descripcion);
      if (workOrderId) form.append("work_order_id", workOrderId);
      else if (projectId) form.append("project_id", projectId);
      else if (selected && tipo === "orden") form.append("work_order_id", selected.id);
      else if (selected && tipo === "obra") form.append("project_id", selected.id);
      if (file) form.append("file", file);
      await api.post("/purchases", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("app.saved"));
      qc.invalidateQueries({ queryKey: ["purchases"] });
      invalidateKeys.forEach((k) => qc.invalidateQueries({ queryKey: k }));
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent data-testid="purchase-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("finance.purchases.form.title")}</DialogTitle>
        </DialogHeader>
        <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
          <label
            htmlFor="purchase-photo-input"
            data-testid="purchase-photo-trigger"
            className="flex h-28 w-full cursor-pointer flex-col items-center justify-center gap-1.5 border border-dashed border-line-strong bg-surface-sunken text-ink-tertiary hover:border-ink-tertiary"
          >
            <Camera className="h-6 w-6" />
            <span className="max-w-[90%] truncate text-[12px]">{file ? file.name : t("finance.purchases.form.photo")}</span>
          </label>
          <input id="purchase-photo-input" type="file" accept="image/*" capture="environment" className="hidden"
            data-testid="purchase-photo-input"
            onChange={(e) => setFile(e.target.files?.[0] || null)} />

          <div>
            <Label>{t("finance.purchases.form.amount")}</Label>
            <Input data-testid="purchase-amount-input" inputMode="decimal" value={monto}
              onChange={(e) => setMonto(e.target.value)} placeholder="0.00" className="mt-1" />
          </div>

          {!preset && (
            <div className="space-y-2 border border-line bg-surface-sunken p-2.5">
              <Label>{t("finance.purchases.form.work_type")}</Label>
              <div className="flex gap-1.5">
                {["orden", "obra", "sin_asignar"].map((opt) => (
                  <Button key={opt} type="button" size="sm" variant={tipo === opt ? "default" : "outline"}
                    data-testid={`purchase-worktype-${opt}`}
                    className={tipo === opt ? "h-7 flex-1 bg-ink text-[11.5px] text-paper" : "h-7 flex-1 text-[11.5px]"}
                    onClick={() => { setTipo(opt); setSelected(null); setQuery(""); }}>
                    {t(`finance.purchases.form.work_type_${opt}`)}
                  </Button>
                ))}
              </div>
              {tipo !== "sin_asignar" && (
                selected ? (
                  <div className="flex items-center justify-between border border-line bg-white px-2 py-1.5 text-[12.5px]">
                    <span data-testid="purchase-selected-work" className="truncate">{selected.label}</span>
                    <button data-testid="purchase-clear-work" onClick={() => setSelected(null)} className="text-ink-tertiary hover:text-ink">×</button>
                  </div>
                ) : (
                  <div className="relative">
                    <Input data-testid="purchase-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
                      placeholder={t("finance.purchases.form.search_placeholder")} className="h-8 text-[12.5px]" />
                    {query.trim().length >= 2 && (
                      <div data-testid="purchase-search-results" className="absolute z-10 mt-1 w-full border border-line bg-white shadow-sm">
                        {results.length ? results.map((r) => (
                          <button key={r.id} data-testid={`purchase-search-result-${r.id}`}
                            onClick={() => { setSelected(r); setQuery(""); }}
                            className="block w-full truncate px-2.5 py-1.5 text-left text-[12.5px] hover:bg-surface-sunken">
                            {r.label}
                          </button>
                        )) : (
                          <p className="px-2.5 py-1.5 text-[12px] text-ink-tertiary">{t("finance.purchases.form.no_results")}</p>
                        )}
                      </div>
                    )}
                  </div>
                )
              )}
              {tipo === "sin_asignar" && (
                <p className="text-[11.5px] text-ink-tertiary">{t("finance.purchases.form.unassigned_hint")}</p>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("finance.purchases.form.date")}</Label>
              <Input data-testid="purchase-date-input" type="date" value={fecha}
                onChange={(e) => setFecha(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label>{t("finance.payments.type")}</Label>
              <Select value={medioPago} onValueChange={setMedioPago}>
                <SelectTrigger data-testid="purchase-method-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MEDIOS_PAGO.map((m) => <SelectItem key={m} value={m}>{t(`finance.payments.types.${m}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>{t("finance.purchases.form.provider")}</Label>
            <Input data-testid="purchase-provider-input" value={proveedor}
              onChange={(e) => setProveedor(e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label>{t("finance.purchases.form.description")}</Label>
            <Textarea data-testid="purchase-description-input" value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)} className="mt-1" rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" data-testid="purchase-cancel-button" onClick={() => onOpenChange(false)}>{t("app.cancel")}</Button>
          <Button data-testid="purchase-submit-button" disabled={busy} onClick={submit} className="bg-ink text-paper hover:opacity-90">
            {t("app.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
